/**
 * 4 点で補正した絵を canvas に描く（座標の確認用。components/QuadSetupEditor.js / components/WarpCheck.js）。
 *
 * 出力（pw × ph）の各画素について、逆の射影変換（出力 → 元の 1 枚）で元の位置を求めて取る＝OpenCV の warpPerspective と同じ
 * 計算を WebGL の fragment shader でする。**CSS の matrix3d で元の 1 枚（5580 幅など）を変形すると Chrome が描かないことがある**
 * （2026-09-30 に旧「撮影の設定」の小さい補正後の絵も真っ黒だった）ので、描く側で変換する。
 *
 * 元は画像（img。一度だけテクスチャにする。CORS 付きで読む）か canvas（配信の映像。毎フレーム取り直す）。WebGL が無ければ何も描かない。
 */
import { useEffect, useRef } from 'react';
import { homographyQuad, warpTarget } from '../utils/fieldPicker';

const VS = `attribute vec2 p; varying vec2 uv; void main(){ uv = (p + 1.0) * 0.5; gl_Position = vec4(p, 0.0, 1.0); }`;
const FS = `precision highp float; varying vec2 uv; uniform sampler2D tex; uniform mat3 H; uniform vec2 dst; uniform vec2 src;
void main(){
  vec2 o = vec2(uv.x, 1.0 - uv.y) * dst;          // 出力の画素（左上原点）
  vec3 s = H * vec3(o, 1.0);
  vec2 q = s.xy / s.z / src;                       // 元の 1 枚の 0..1
  if (q.x < 0.0 || q.y < 0.0 || q.x > 1.0 || q.y > 1.0) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
  gl_FragColor = texture2D(tex, q);
}`;

/** 出力の画素 → 元の 1 枚の画素 の 3×3（列優先。WebGL の mat3 に渡す形） */
export function inverseWarp(points, pw, ph) {
  const h = homographyQuad(warpTarget(pw, ph), points);
  if (!h) return null;
  const [a, b, c, d, e, f, g, k] = h;
  return [a, d, g, b, e, k, c, f, 1];
}

function setup(gl) {
  const sh = (type, code) => { const s = gl.createShader(type); gl.shaderSource(s, code); gl.compileShader(s); return s; };
  const prog = gl.createProgram();
  gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
  gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
  gl.linkProgram(prog);
  gl.useProgram(prog);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return {
    H: gl.getUniformLocation(prog, 'H'), dst: gl.getUniformLocation(prog, 'dst'), src: gl.getUniformLocation(prog, 'src'),
  };
}

/**
 * @param {object} props
 * @param {number[][]} props.points 元の 1 枚の画素で 4 点（TL, TR, BR, BL）
 * @param {number} props.pw 補正後の幅
 * @param {number} props.ph 補正後の高さ
 * @param {string} [props.imgSrc] 元が画像のとき
 * @param {{current: HTMLCanvasElement}} [props.sourceCanvasRef] 元が配信の映像（canvas）のとき
 */
export default function WarpCanvas({ points, pw, ph, imgSrc = null, sourceCanvasRef = null, style = {}, ...rest }) {
  const ref = useRef(null);
  const state = useRef({ gl: null, loc: null, img: null, ready: false });
  const pointsRef = useRef(points);
  pointsRef.current = points;

  useEffect(() => {
    const c = ref.current;
    const gl = c?.getContext?.('webgl', { preserveDrawingBuffer: true });
    if (!gl) return undefined;
    const st = state.current;
    st.gl = gl;
    st.loc = setup(gl);
    let raf = 0;
    let alive = true;
    const draw = (source) => {
      const w = source.naturalWidth || source.width;
      const h = source.naturalHeight || source.height;
      const m = pointsRef.current && inverseWarp(pointsRef.current, pw, ph);
      if (!w || !h || !m) return;
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
      gl.viewport(0, 0, c.width, c.height);
      gl.uniformMatrix3fv(st.loc.H, false, m);
      gl.uniform2f(st.loc.dst, pw, ph);
      gl.uniform2f(st.loc.src, w, h);     // 点は元の 1 枚の実寸の画素（QuadSetupEditor の W × H）
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };
    st.redraw = () => {};
    if (sourceCanvasRef) {
      const tick = () => {
        if (!alive) return;
        const s = sourceCanvasRef.current;
        if (s?.width) draw(s);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    } else if (imgSrc) {
      const img = new Image();
      img.crossOrigin = 'anonymous';   // WebGL のテクスチャにするので CORS 付きで（メディアの CloudFront は ACAO *）
      img.onload = () => { if (alive) { st.img = img; draw(img); } };
      img.src = imgSrc;
      st.redraw = () => { if (st.img) draw(st.img); };
    }
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, [imgSrc, sourceCanvasRef, pw, ph]);

  // 点が変わったら描き直す（画像のとき。配信は毎フレーム描いている）
  useEffect(() => { state.current.redraw?.(); }, [points]);

  return <canvas ref={ref} width={pw} height={ph} style={{ display: 'block', ...style }} {...rest} />;
}
